import React, { useEffect, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Stack, TextField, FormControl } from '@mui/material';
import Typography from "@material-ui/core/Typography";
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';

import './button.css';

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.padding,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(2),
    }
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

export const options = {
    responsive: true,
    plugins: {
        legend: {
            position: 'top',
            display: true,
            labels: {
                generateLabels: (chart) => {
                    const datasets = chart.data.datasets;
        
                    return datasets.map((dataset, index) => {
                        const total = dataset.data.reduce((acc, value) => acc + Math.round(value), 0); // Garante números inteiros
                        return {
                            text: `${dataset.label.split('(')[0]} (${total})`, // Atualiza o texto da legenda
                            fillStyle: dataset.backgroundColor,
                            hidden: !chart.isDatasetVisible(index),
                            datasetIndex: index,
                        };
                    });
                },
            },
        },        
        title: {
            display: true,
            text: 'Gráfico de Atendimento',
            position: 'left',
        },
        datalabels: {
            display: true,
            anchor: 'start',
            offset: -30,
            align: "start",
            color: "#fff",
            textStrokeColor: "#000",
            textStrokeWidth: 2,
            font: {
                size: 20,
                weight: "bold",
            },
        },
    },
    scales: {
        y: {
            beginAtZero: true, 
            ticks: {
                stepSize: 1,
                precision: 0,
                callback: function(value) {
                    return Number.isInteger(value) ? value : null;
                },
            },
        },
    },
};


export const ChatsQueueFilter = () => {
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [allUserNames, setAllUserNames] = useState([]); // Estado para todos os usuários
    const [ticketsData, setTicketsData] = useState({ data: [] });
    const [personName, setPersonName] = useState(["Todas"]);

    // Totais dinâmicos
    const [totals, setTotals] = useState({
        emAndamento: 0,
        encerrados: 0,
        pendentes: 0,
    });

    const companyId = localStorage.getItem("companyId");

    useEffect(() => {
        handleGetTicketsInformation();
    }, []);

    useEffect(() => {
        // Atualiza todos os nomes de usuários no estado allUserNames
        const uniqueNames = [...new Set(ticketsData?.data.map((item) => item.nome))];
        setAllUserNames((prevNames) => [...new Set([...prevNames, ...uniqueNames])]);

        // Calcula os totais dinâmicos
        const totalAndamento = ticketsData?.data.reduce((acc, item) => acc + (item.em_andamento || 0), 0);
        const totalEncerrados = ticketsData?.data.reduce((acc, item) => acc + (item.encerrados || 0), 0);
        const totalPendentes = ticketsData?.data.reduce((acc, item) => acc + (item.pendentes || 0), 0);

        setTotals({
            emAndamento: totalAndamento,
            encerrados: totalEncerrados,
            pendentes: totalPendentes,
        });
    }, [ticketsData]);

    const nomesUnicos = allUserNames;

    const dataCharts = {
        labels: nomesUnicos,
        datasets: [
            {
                label: `Atendimentos em Andamento`,
                backgroundColor: "#1FA0DB",
                data: nomesUnicos.map((nome) => {
                    const fila = ticketsData?.data.find((item) => item.nome === nome);
                    return fila ? fila.em_andamento : 0;
                }),
            },
            {
                label: `Atendimentos Encerrados`,
                backgroundColor: "#B5D336",
                data: nomesUnicos.map((nome) => {
                    const fila = ticketsData?.data.find((item) => item.nome === nome);
                    return fila ? fila.encerrados : 0;
                }),
            },
            {
                label: `Atendimentos Pendentes`,
                backgroundColor: "#F49445",
                data: nomesUnicos.map((nome) => {
                    const fila = ticketsData?.data.find((item) => item.nome === nome);
                    return fila ? fila.pendentes : 0;
                }),
            },
        ],
    };

    const handleGetTicketsInformation = async () => {
        try {
            const { data } = await api.get(
                `/dashboard/ticketsFilteredQueues?initialDate=${format(initialDate, "yyyy-MM-dd")}&finalDate=${format(
                    finalDate,
                    "yyyy-MM-dd"
                )}&companyId=${companyId}&filas=${personName}`
            );

            setTicketsData(data);
        } catch (error) {
            toast.error("Erro ao obter informações da conversa");
        }
    };

    const handleChange = (event) => {
        const { value } = event.target;

        if (value.includes("Todas")) {
            // Se "Todos" for selecionado, seleciona todos os nomes
            if (personName.includes("Todas")) {
                setPersonName([]); // Desmarca tudo se "Todos" já estiver selecionado
            } else {
                setPersonName(nomesUnicos); // Seleciona todos os nomes
            }
        } else {
            setPersonName(value); // Seleção padrão
        }
    };

    return (
        <>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                Total de Atendimentos por Fila
            </Typography>

            <Stack direction={"row"} spacing={2} alignItems={"center"} sx={{ my: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                    <DatePicker
                        value={initialDate}
                        onChange={(newValue) => {
                            setInitialDate(newValue);
                        }}
                        label="Inicio"
                        renderInput={(params) => <TextField fullWidth {...params} sx={{ width: "20ch" }} />}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                    <DatePicker
                        value={finalDate}
                        onChange={(newValue) => {
                            setFinalDate(newValue);
                        }}
                        label="Fim"
                        renderInput={(params) => <TextField fullWidth {...params} sx={{ width: "20ch" }} />}
                    />
                </LocalizationProvider>

                <FormControl style={{ minWidth: "200px" }}>
                    <InputLabel id="demo-multiple-checkbox-label">Filas</InputLabel>
                    <Select
                        labelId="demo-multiple-checkbox-label"
                        id="demo-multiple-checkbox"
                        multiple
                        value={personName}
                        onChange={handleChange}
                        input={<OutlinedInput label="Filas" />}
                        renderValue={(selected) => {
                            if (selected.length === nomesUnicos.length || selected.includes("Todas")) {
                                return "Todas";
                            }
                            return selected.join(", ");
                        }}
                        MenuProps={MenuProps}
                    >
                        {/* Opção "Todos" */}
                        <MenuItem key="Todas" value="Todas">
                            <Checkbox checked={personName.length === nomesUnicos.length || personName.includes("Todas")} />
                            <ListItemText primary="Todas" />
                        </MenuItem>

                        {/* Nomes individuais */}
                        {nomesUnicos?.map((nome) => (
                            <MenuItem key={nome} value={nome}>
                                <Checkbox checked={personName.includes(nome)} />
                                <ListItemText primary={nome} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button className="buttonHover" onClick={handleGetTicketsInformation} variant="contained">
                    Filtrar
                </Button>
            </Stack>
            <Bar options={options} data={dataCharts} style={{ maxWidth: "100%", maxHeight: "280px" }} />
        </>
    );
};